// @ts-strict-ignore
import type { WorkflowState } from 'flow/store/redux/state';
import type { CanvasRect } from 'flow/store/redux/types';
import type {
  Graph,
} from '../types';

import type { WorkflowEdge } from '../edges';
import {
  computeEdgeState, getEdgeZIndex,
} from '../edges';

import {
  CANVAS_VERTICAL_PADDING,
  EXPANDED_SNIPPET_X_OFFSET,
  LEFT_CHILD_PADDING,
  NODE_WIDTH,
  SNIPPET_Y_HOIST,
  XL_NODE_FIRST_ROW_HOIST,
} from './constants';
import type {
  NodeLineage,
  BidirectionalGraphTopology,
  AdjacencyMap,
  MaxPathLengthMap,
} from './types';
import type { WorkflowNode } from '../nodes';
import {
  computeNodeState,
  getNodeZIndex,
  indexWorkflowNodes,
  isContainerNode,
  isExpandedSnippetNode,
  isFolderNode,
  isInputAttributeNode,
  isRiskCalculationNode,
  isSnippetInternalChildNode,
  isSnippetNode,
} from '../nodes';
import { sortGraphBeforePlacement } from './sorting/sorting';
import type { Action, PublicId } from 'flow/store/engine/types';
import { isActionIdOfType, isActionOfType } from 'flow/service/actions';
import { ActionType } from 'flow/store/engine/constants';
import type { NodeDimensions, NodePosition } from './nodeMatrix';
import {
  allocate,
  allocateSubgraphOwnership,
  createDefaultNodeMatrix,
  defaultNodeDimensions,
  getDimensions,
  getNextAvailableRowIndex,
  getNodeColumn,
  getNodePosition,
  getSnippetNodeDimensions,
} from './nodeMatrix';
import type { XYPosition } from 'react-flow-renderer';

/**
 * builds a Map mapping a node id and the maximum number of nodes it has connected to it
 * @param nodes a list of all the nodes in the graph
 * @param incomers Map containing index of node ids and the ids of nodes connected to it
 * @param snippetDimensions Optional map of snippet -> snippet graph dimensions.
 *   If provided, drops in the value of the subgraph width for a snippet for downstream actions
 * @returns Map with node id to depth relationship
 */
export function getMaxPathLengthMap(args: {
  nodes: WorkflowNode[];
  incomers: AdjacencyMap;
  subgraphDimensions?: Record<PublicId, NodeDimensions>;
}): MaxPathLengthMap {
  const { incomers, nodes, subgraphDimensions } = args;
  const hasSubgraphs = Boolean(subgraphDimensions);

  const indexedNodes = indexWorkflowNodes(nodes);
  const maxPathLengthMap: MaxPathLengthMap = new Map();

  function getEdgeLength(dependencyId: string, nodeId: string): number {
    // if no subgraphs are present, then every edge has a length of 1
    if (!hasSubgraphs) return 1;

    const isSnippet = isActionIdOfType(dependencyId, ActionType.SNIPPET);
    if (!isSnippet) return 1;

    // only actions external to the snippet should be pushed by the snippet width
    const node = indexedNodes[nodeId];
    const snippetOffset = isSnippetInternalChildNode(node)
      ? 0
      : subgraphDimensions[dependencyId].nodeWidth;

    return Math.max(1, snippetOffset);
  }

  function traverseDependencies(id: string): number {
    const pathMemo = maxPathLengthMap.get(id);
    if (pathMemo !== undefined) return pathMemo;

    const deps = incomers.get(id);
    if (!deps || deps.size === 0) {
      maxPathLengthMap.set(id, 0);
      return 0;
    }

    let maxPath = -Infinity;
    for (const depId of deps) {
      const pathLength = traverseDependencies(depId) + getEdgeLength(depId, id);
      maxPath = Math.max(maxPath, pathLength);
    }

    maxPathLengthMap.set(id, maxPath);
    return maxPath;
  }

  // Step 3. Kickoff to compute paths for all nodes
  for (const node of nodes) {
    traverseDependencies(node.id);
  }

  return maxPathLengthMap;
}


/**
 * Builds a topology bundle mapping all node ids to their direct incomer and outgoer node ids.
 * @param graph Graph with nodes and edges
 * @param nodeContainerState Node folder & Snippet collapsed states
 * @returns a bundle with incomer and outgoer connections per node
 */
export function getBidirectionalGraphTopology(
  graph: Graph,
  nodeContainerState: WorkflowState['ui']['nodeContainers'],
): BidirectionalGraphTopology {
  // maps a nodeId to all nodeIds that connect to that nodeId
  const incomers: AdjacencyMap = new Map();
  // maps a nodeId to all nodeIds that the nodeId connect to
  const outgoers: AdjacencyMap = new Map();
  const { inputAttribute: nodeFolderState, snippets: snippetCollapsedState } = nodeContainerState;


  // index the nodes into a Map by their ID for a faster lookup
  const nodesIndexedById = graph.nodes.reduce((indexedNodes, node) => {
    indexedNodes.set(node.id, node);
    if (isContainerNode(node)) {
      const state = isFolderNode(node) ? nodeFolderState : snippetCollapsedState[node.id];
      if (state.isCollapsed) {
        // add container children to topology if the container is collapsed
        node.data.children.forEach(child => indexedNodes.set(child.id, child));
      }
    }
    return indexedNodes;
  }, new Map<WorkflowNode['id'], WorkflowNode>());

  /**
   * we build 2 data structures of the same type (AdjacencyMap) in a single pass;
   * one for incomer connections (connection _to_ a node) and another for outgoer
   * connections (connections _from_ a node).
   */
  graph.edges.forEach(({ source: sourceId, target: targetId }) => {
    if (nodesIndexedById.get(sourceId)?.data?.layout.asIncomer) {
      if (!incomers.has(targetId)) {
        incomers.set(targetId, new Set());
      }
      incomers.get(targetId).add(sourceId);
    }
    if (nodesIndexedById.get(targetId)?.data?.layout.asOutgoer) {
      if (!outgoers.has(sourceId)) {
        outgoers.set(sourceId, new Set());
      }
      outgoers.get(sourceId).add(targetId);
    }
  });

  return {
    incomers,
    outgoers,
  };
}

/**
 * @param graph graph of nodes and edges
 * @param node source node from which to find all connected node ids
 * @returns object containing Sets of Node ids organized by their ancestry relationship to the Node
 */
export function getAllNodeIdsConnectedToNode(
  nodeId: WorkflowNode['id'], graphTopology: BidirectionalGraphTopology,
): NodeLineage {
  if (!nodeId) {
    return {
      nodeId: null,
      directIncomers: new Set(),
      directOutgoers: new Set(),
      extendedIncomers: new Set(),
      extendedOutgoers: new Set(),
      all: new Set(),
    };
  }

  const directIncomerIdsSet: Set<WorkflowNode['id']> = new Set();
  const extendedIncomerIdsSet: Set<WorkflowNode['id']> = new Set();
  const directOutgoersIdsSet: Set<WorkflowNode['id']> = new Set();
  const extendedOutgoersIdsSet: Set<WorkflowNode['id']> = new Set();
  const allConnectedNodeIdsSet: Set<WorkflowNode['id']> = new Set();


  // recurse up the node incomers and append their ids
  function appendIncomerNodeIds(_nodeId: WorkflowNode['id']) {
    const incomerNodeIdsForNode = graphTopology.incomers.get(_nodeId);
    if (!incomerNodeIdsForNode) {
      return;
    }

    for (const incomerNodeId of Array.from(incomerNodeIdsForNode.values())) {
      if (_nodeId === nodeId) {
        directIncomerIdsSet.add(incomerNodeId);
      } else {
        extendedIncomerIdsSet.add(incomerNodeId);
      }
      allConnectedNodeIdsSet.add(incomerNodeId);
      appendIncomerNodeIds(incomerNodeId);
    }
  }

  // recurse down the node outgoers and append their ids
  function appendOutgoerNodeIds(_nodeId: WorkflowNode['id']) {
    const outgoerNodeIdsForNode = graphTopology.outgoers.get(_nodeId);
    if (!outgoerNodeIdsForNode) {
      return;
    }

    for (const outgoerNodeId of Array.from(outgoerNodeIdsForNode.values())) {
      if (_nodeId === nodeId) {
        directOutgoersIdsSet.add(outgoerNodeId);
      } else {
        extendedOutgoersIdsSet.add(outgoerNodeId);
      }
      allConnectedNodeIdsSet.add(outgoerNodeId);
      appendOutgoerNodeIds(outgoerNodeId);
    }
  }

  allConnectedNodeIdsSet.add(nodeId);
  appendIncomerNodeIds(nodeId);
  appendOutgoerNodeIds(nodeId);

  return {
    nodeId,
    directIncomers: directIncomerIdsSet,
    directOutgoers: directOutgoersIdsSet,
    extendedIncomers: extendedIncomerIdsSet,
    extendedOutgoers: extendedOutgoersIdsSet,
    all: allConnectedNodeIdsSet,
  };
}


/**
 * Returns the actions that directly depend on the given node.
 *  - For snippet actions, only outgoers of snippet children that are not themselves snippet children
 *  are considered direct dependents
 *  - For all other action types, direct outgoers from node lineage are used.
 *
 * @param args.nodeLineage - Lineage for the node (nodeId and directOutgoers)
 * @param args.graphTopology - Bidirectional graph topology for traversing connections
 * @param args.actions - Map of action id to Action, or null
 * @returns Array of Action objects for direct dependent nodes
 */
export function getDirectDependentActions(args: {
  nodeLineage: NodeLineage;
  graphTopology: BidirectionalGraphTopology;
  actions: Record<string, Action> | null;
}): Action[] {
  const { nodeLineage, graphTopology, actions } = args;
  const { nodeId, directOutgoers } = nodeLineage;
  if (!actions) return [];

  if (isActionOfType(actions[nodeId], ActionType.SNIPPET)) {
    // Snippet dependents = outgoers of snippet children that are not themselves snippet children
    const snippetDependentNodeIds = new Set<string>();
    for (const snippetChildId of directOutgoers) {
      const { directOutgoers: grandchildIds } = getAllNodeIdsConnectedToNode(
        snippetChildId,
        graphTopology,
      );
      for (const id of grandchildIds) {
        if (!directOutgoers.has(id)) snippetDependentNodeIds.add(id);
      }
    }
    return Array.from(snippetDependentNodeIds).flatMap(id => actions[id] ?? []);
  }

  // For all other types, directOutgoers is an accurate representation of direct dependents
  return Array.from(directOutgoers).flatMap(id => actions[id] ?? []);
}


/**
 * Split out the nodes in a graph by their layout mode
 * @param graph graph of nodes and edges
 * @returns object containing all nodes split in two categories: auto and manual layout modes
 */
export function splitGraphNodesByLayoutMode(graph: Graph): {
  autoLayoutNodes: WorkflowNode[]; manualLayoutNodes: WorkflowNode[];
} {
  const splitNodes = graph.nodes.reduce((_splitNodes, node) => {
    if (!node.data || node.data.layout.autoLayout) {
      _splitNodes.autoLayoutNodes.push(node);
    } else {
      _splitNodes.manualLayoutNodes.push(node);
    }


    return _splitNodes;
  }, { autoLayoutNodes: [], manualLayoutNodes: [] });


  return splitNodes;
}

/**
 * Adjusts the position of the elements that need to stay in place as the user pans and zooms the canvas.
 */
export function adjustLayoutToFreezeFixedNodes(graph: Graph, canvasRect: CanvasRect) {
  graph.nodes.forEach(node => {
    if (node.data?.layout.fixed) {
      const { x: currentX, y: currentY } = node.position;

      node.position = {
        x: (currentX - canvasRect.x) / canvasRect.zoom,
        y: (currentY - canvasRect.y) / canvasRect.zoom,
      };
    }
  });
}

/**
 * moves the outcome construction nodes up top to create the connection effect
 */
export function adjustOutcomeConstructionNodes(graph: Graph, focusedNodeId: WorkflowState['ui']['focusedNodeId']) {
  const focusedNode = graph.nodes.find(node => node.id === focusedNodeId);

  if (focusedNode?.type === 'outcome') {
    focusedNode.position = {
      ...focusedNode.position,
      y: CANVAS_VERTICAL_PADDING,
    };
  }
}

function isSnippetEdge(
  edge: WorkflowEdge,
  indexedNodes: Record<WorkflowNode['id'], WorkflowNode>,
): boolean {
  const sourceNode = indexedNodes[edge.source];

  return isSnippetInternalChildNode(sourceNode);
}

function getXOffset(
  node: WorkflowNode,
  hasInputAttributeNodeFolder: boolean,
) {
  if (isExpandedSnippetNode(node)) {
    return EXPANDED_SNIPPET_X_OFFSET;
  }

  if (hasInputAttributeNodeFolder && isInputAttributeNode(node)) {
    return LEFT_CHILD_PADDING;
  }

  return 0;
}

function getYOffset(
  node: WorkflowNode,
  rowIndex: number,
) {
  if (isSnippetNode(node)) {
    return SNIPPET_Y_HOIST;
  }

  if (rowIndex === 0 && isRiskCalculationNode(node)) {
    return XL_NODE_FIRST_ROW_HOIST;
  }

  return 0;
}

function getAutoLayoutPosition(args: {
  position: NodePosition;
  node: WorkflowNode;
  rowIndex: number;
  hasInputAttributeNodeFolder: boolean;
}): XYPosition {
  const {
    position,
    node,
    rowIndex,
    hasInputAttributeNodeFolder,
  } = args;


  const xOffset = getXOffset(node, hasInputAttributeNodeFolder);
  const yOffset = getYOffset(node, rowIndex);

  const defaultNodePosition = getNodePosition(position);

  return {
    x: defaultNodePosition.x + xOffset,
    y: defaultNodePosition.y + yOffset,
  };
}

/**
 * this is the entry function for the lay-outing logic.
 * set the position of the existing nodes in the graph
 */
export function layOutGraph({
  graph,
  uiStatePartial,
  nodeLineage,
  maxIncomingPathLengthMap,
  subGraphDimensions,
  hasInputAttributeNodeFolder,
}: {
  graph: Graph;
  uiStatePartial: {
    activeNodeEditorTab: WorkflowState['ui']['activeNodeEditorTab'];
    canvasWidth: WorkflowState['ui']['canvasProperties']['rect']['width'];
    floatedAnchorNodeId: WorkflowState['ui']['floatedAnchorNodeId'];
    focusedNodeId: WorkflowState['ui']['focusedNodeId'];
    highlightedNodeId: WorkflowState['ui']['highlightedNodeId'];
    hoveredNodeId: WorkflowState['ui']['hoveredNodeId'];
    nodeFolder: WorkflowState['ui']['nodeContainers']['inputAttribute'];
  };
  nodeLineage: NodeLineage;
  maxIncomingPathLengthMap: MaxPathLengthMap;
  subGraphDimensions: Record<PublicId, NodeDimensions>;
  hasInputAttributeNodeFolder?: boolean;
}): void {
  /**
   * sorting the nodes prior to their placement allows us to customize the order in which
   * they are placed in the graph without making changes to the positional algorithm
  */
  sortGraphBeforePlacement(
    graph,
    uiStatePartial,
    nodeLineage,
  );

  const {
    autoLayoutNodes,
    manualLayoutNodes,
  } = splitGraphNodesByLayoutMode(graph);

  const indexedNodes = indexWorkflowNodes(graph.nodes);

  // we auto layout nodes in 2 steps:
  // 1. allocate ownership/space for subgraphs within the matrix
  // 2. populate the matrix with nodes & compute their x,y positions using their row & column indice

  const { nodeHeight, nodeWidth } = getDimensions({
    maxPathLengthMap: maxIncomingPathLengthMap,
    nodeIds: autoLayoutNodes.map(node => node.id),
    subGraphDimensions,
  });

  const nodeMatrix = createDefaultNodeMatrix({ nodeWidth, nodeHeight });

  // a map from node id -> node's position in the node matrix (column, row)
  const nodeMatrixLocationMap: Record<WorkflowNode['id'], NodePosition> = {};

  allocateSubgraphOwnership({
    matrix: nodeMatrix,
    nodes: autoLayoutNodes,
    subGraphDimensions,
    maxIncomingPathLengthMap,
  });

  autoLayoutNodes.forEach(node => {
    const columnIndex = getNodeColumn(maxIncomingPathLengthMap, node);
    const column = nodeMatrix[columnIndex];
    const rowIndex = getNextAvailableRowIndex(column, node);

    nodeMatrixLocationMap[node.id] = { column: columnIndex, row: rowIndex };

    const dimensions = isExpandedSnippetNode(node)
      ? getSnippetNodeDimensions(subGraphDimensions[node.id])
      : defaultNodeDimensions;

    if (rowIndex !== -1) {
      allocate({
        matrix: nodeMatrix,
        position: { column: columnIndex, row: rowIndex },
        dimensions,
        nodeId: node.id,
      });

      node.position = getAutoLayoutPosition({
        position: { column: columnIndex, row: rowIndex },
        node,
        rowIndex,
        hasInputAttributeNodeFolder,
      });
    } else {
      node.position = { x: 0, y: 0 };
    }

    const nodeState = computeNodeState({
      nodePartial: { nodeId: node.id },
      nodeLineage,
      uiStatePartial,
    });
    node.zIndex = getNodeZIndex(node, nodeState);
  });

  /**
   * Manually place nodes that need to be
   */
  let precedenceIndexedNodeCount = 0;
  manualLayoutNodes.forEach(node => {
    /**
     * since outcomes and workflow routing nodes are sorted by precedence, we can use their local
     * index as their row. the column pixels (y) are manually computed by taking in the value of
     * the canvas width. this places them outside of the visible canvas, flush to its right
     * edge.
     */
    if (node.type === 'outcome' || node.type === 'workflow') {
      const x = uiStatePartial.canvasWidth;
      // we don't care about the column so we pass a `0`
      const { y } = getNodePosition({ row: precedenceIndexedNodeCount, column: 0 });
      node.position = { x, y };
      node.zIndex = getNodeZIndex(node);
      precedenceIndexedNodeCount += 1;
    }

    /**
     * importedTailNodes can be placed at the top row and far away into the left edge to
     * accomplish the desired effect without having to manually align them to their corresponding
     * imported node
     */
    if (node.type === 'importedTailNode') {
      const x = 0 - (2 * NODE_WIDTH);
      const { y } = getNodePosition({ row: 0, column: 0 });
      node.position = { x, y };
      node.zIndex = getNodeZIndex(node);
    }
  });

  graph.edges.forEach(edge => {
    const edgeState = computeEdgeState({
      sourceId: edge.source,
      targetId: edge.target,
      nodeLineage,
      uiStatePartial,
    });

    edge.zIndex = getEdgeZIndex(
      edgeState,
      isSnippetEdge(edge, indexedNodes),
    );
  });
}
